/**
 * Orders Header component
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { WdLocalEnvironmentService } from '../../../../../services/environment/wd-local-environment.service';
import { ManageLookaheadsComponent } from '../../../../../components/modals/order-lookaheads/manage-lookaheads.component';
import { WdJobService } from '../../../../../services/jobs/wd-jobs.service';
import { WdLocalSharedFunctionsService } from '../../../../../services/shared/wd-local-shared-functions.service';
import { WdOrdersV2Service } from '../../../../../services/orders/wd-orders-v2.service';
import { OrderFiltersComponent } from '../../../../../components/modals/order-filters/order-filters.component';
import { IBcxErrorService } from '../../../../../interfaces/generic/generic-interfaces';
import { OrderCustomTabComponent } from '../../../../../components/modals/order-custom-tab/order-custom-tab.component';
import { IOrderTabs } from '../../../../../interfaces/orders/order-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { WdUserService } from '../../../../../services/users/wd-users.service';

@Component({
  selector: 'app-orders-list-v2-header-component',
  templateUrl: './header-v2.component.html',
  styleUrls: ['./header-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ListV2HeaderComponent implements OnInit {
  // Input titles
  @Input() pageTitle?: string;
  @Input() pageSubtitle?: string;

  // Boolean inputs
  @Input() showHeader?: boolean = true;
  @Input() showFilters?: boolean = true;

  // Callbacks
  @Output() tabChanges: EventEmitter<any> = new EventEmitter();

  // Maximum number of custom tabs allowed
  maxCustomTabs?: number = 3;

  // String variables
  workOrdersCsvUrl = '';

  /**
   * Constructor Initialization
   * @since 4.71.0
   */
  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private matDialog: MatDialog,
    private translateService: TranslateService,
    public wdEnvironmentService: WdLocalEnvironmentService,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    public wdJobService: WdJobService,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdOrdersService: WdOrdersV2Service,
    public wdUserService: WdUserService,
  ) {}

  // Order String Inputs
  @Input() orderType: string = this.wdLocalSharedFunctions.orderTypeKey.WORK;

  // Update Status
  updateStatusesValue: any = null;
  updatingStatuses = false;

  /**
   * Update statuses for selected orders
   * @since 4.71.0
   */
  async updateOrderStatuses(status: any): Promise<void> {
    this.updatingStatuses = true;
    try {
      const indexes: any = [];
      this.wdOrdersService.allOrdersSelected.selected.forEach((order: any) => {
        // if (order.status.type !== status.type) {
        indexes.push(order.index);
        // }
      });
      // Update Status
      await this.wdOrdersService.updateOrderStatuses(indexes, status?.type);
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Reload Orders @todo Reload ensures filtering is correct. Can we improve efficiency here?
      await this.wdOrdersService.fetchOrders(false);
      this.updatingStatuses = false;
      setTimeout(() => {
        this.updateStatusesValue = null;
        this.wdOrdersService.allOrdersSelected.clear();
      }, 0);
    }
  }

  /**
   * Open Add Lookahead Window
   * @since 4.71.0
   */
  addLookahead() {
    // Open Lookahead Dialog
    this.matDialog.open(ManageLookaheadsComponent, {
      disableClose: true,
      data: {
        onSuccessCallback: () => {
          // Reload Orders
          this.reloadOrders();
        },
      },
    });
  }

  /**
   * Triggered when filters are changed
   * @since 4.71.0
   */
  filtersChanged(): void {
    // Save Selected API Filters
    this.wdOrdersService.saveSelectedOrderFilters();
    // Reload Orders
    this.reloadOrders();
    // Fetch the Work Orders CSV URL using the updated filters
    this.updateWorkOrdersCsvUrl();
  }

  /**
   * Go to first page of orders
   * @since 4.71.0
   */
  goFirstPage() {
    this.resetNavigation();
    this.filtersChanged();
  }

  /**
   * Go to next page of orders
   * @since 4.71.0
   */
  goNextPage() {
    this.wdOrdersService.orderViewSettings.page++;
    const orders: Array<any> = this.wdOrdersService.allOrders.value;
    // Set Last ID Filter
    if (orders.length > 0) {
      this.wdOrdersService.orderFilters.value.lastId =
        orders[orders.length - 1].id;
    }
    this.filtersChanged();
  }

  /**
   * Handle Custom Tab Button Click (Opens Modal)
   * @since 4.77.0
   * @memberof ListV2HeaderComponent
   */
  handleCustomTabClick(selectedTab?: IOrderTabs) {
    // Open Dialog
    this.matDialog.open(OrderCustomTabComponent, {
      width: '600px',
      data: {
        orderType: this.orderType,
        selectedTab: selectedTab,
        onSuccessCallback: () => {
          this.tabChanges.emit();
        },
      },
    });
  }

  /**
   * Handle Filters Button Click (Opens Filters Modal)
   * @since 4.71.0
   */
  handleFiltersClick() {
    // Open Dialog
    this.matDialog.open(OrderFiltersComponent, {
      width: '600px',
      data: {
        filters: this.wdOrdersService.orderFilters,
        changesCallback: () => {
          return this.filtersChanged();
        },
      },
    });
  }

  /**
   * On Init
   */
  ngOnInit() {
    // Initialize the work orders CSV only once on page init.
    this.updateWorkOrdersCsvUrl();
  }

  /**
   * Update the Work Orders CSV Url with the current filters
   */
  updateWorkOrdersCsvUrl() {
    this.workOrdersCsvUrl = this.wdOrdersService.getWorkOrdersCsvUrl();
  }

  /**
   * Reset Navigation
   * @since 4.71.0
   */
  resetNavigation() {
    this.wdOrdersService.orderViewSettings.page = 1;
    this.wdOrdersService.orderFilters.value.lastId = '';
  }

  /**
   * Reload Orders
   * @since 4.71.0
   */
  reloadOrders() {
    // Reload Orders with API Filtering
    void this.wdOrdersService.fetchOrders(false);
  }

  /**
   * Set Page Size Options
   *
   * @since 4.71.0
   */
  setPageSize($size: any, triggerFiltering = false): void {
    // Reset Navigation
    this.resetNavigation();
    // Set Page Size Filter
    this.wdOrdersService.orderViewSettings.pageSize = $size;
    // Reload Orders
    if (triggerFiltering) {
      this.filtersChanged();
      // Save Settings to Local Storage
      this.wdOrdersService.saveLocalStorageSettings();
    }
  }

  /**
   * Set Toggled Columns
   * @since 4.71.0
   */
  setToggledColumns(): void {
    this.wdOrdersService.setVisibleOrderColumns(
      this.orderType,
      this.wdOrdersService.orderViewSettings.selectedTab?.key,
    );
  }

  /**
   * Toggle default tab visibility
   */
  async setToggledDefaultTabs(tabClicked: IOrderTabs): Promise<void> {
    let currentTab: IOrderTabs = this.wdOrdersService.orderViewSettings
      .selectedTab;
    const visibleTabs: Array<IOrderTabs> = this.wdOrdersService.tabbedOrderContent.filter(
      (tab: IOrderTabs) => tab.isVisible,
    );
    const currentTabIsHidden = !visibleTabs.find(
      (tab: IOrderTabs) => tab.key === currentTab?.key,
    );
    if (visibleTabs.length === 0) {
      // All tabs are hidden
      // Don't allow user to deselect all tabs
      const error = this.translateService.instant('labels.error');
      const errorTabToggle = this.translateService.instant(
        'errors.errorTabToggle',
      );
      this.wdErrorService.userError(error, errorTabToggle);
      // Note: Timeout required to avoid UI issues
      setTimeout(() => {
        // Reselect current tab
        tabClicked.isVisible = true;
        this.changeDetectorRef.detectChanges();
      });
    } else {
      if (currentTabIsHidden) {
        // Current tab is no longer visible
        // Select and show first visible table
        currentTab = visibleTabs[0];
        this.wdOrdersService.orderViewSettings.selectedTab = currentTab;
        this.wdOrdersService.initColumnToggleOptions(
          this.orderType,
          currentTab?.key,
        );
      }
      // Get default tabs
      const defaultTabs: Array<IOrderTabs> = this.wdOrdersService.tabbedOrderContent
        .filter((tab: IOrderTabs) => !tab.isCustom)
        .map((tab: IOrderTabs) => {
          return { key: tab.key, isVisible: tab.isVisible };
        });
      // Add/update tab and then reinitialize tabs
      await this.wdUserService.saveDefaultTabSettings(
        this.wdEnvironmentService.user.index,
        defaultTabs,
      );
    }
  }

  /**
   * On Blur Job Typeahead
   * @since 4.71.0
   */
  typeaheadJobBlur(model: any) {
    // Clear value if no model selected
    if (!model?.index) {
      this.wdOrdersService.orderFilters.value.jobFilter = null;
    }
  }
}
